import styled, { createGlobalStyle } from 'styled-components';

import { Text } from '@/nova/components';
import { cardHover } from '@/nova/theme/configurator-keyframes';
import { CFG, CFG_CHROME, CFG_MOBILE } from '../../../../../../theme/configurator-tokens';
import { FINISH, FINISH_GRADIENTS } from '../../finish.tokens';
import { LAPTOP_FRAME_FINISH } from '../DevicePreview';

export const Root = styled.div`
  position: relative;
  display: flex;
  /* Dynamic viewport height, NOT 100vh: on iPad/iOS Safari 100vh is the largest
     (toolbars-hidden) viewport, so this full-bleed screen overruns the visible area
     and clips its bottom copy/CTA behind the browser chrome. 100dvh fits the
     currently visible viewport. Matches the wizard Shell fix. */
  height: 100dvh;
  width: 100%;
  flex-direction: column;
  overflow: hidden;
  background: ${FINISH.successBg};
`;

export const Bloom = styled.div<{ $img?: string }>`
  pointer-events: none;
  position: absolute;
  inset: 0;
  background-image: ${FINISH_GRADIENTS.successBloomDesktop};

  ${CFG_MOBILE} {
    /* Colourful bloom photo, darkened to the edges by the vignette on top. */
    background-image: ${FINISH_GRADIENTS.successVignetteMobile}, url(${({ $img }) => $img});
    background-size: cover, cover;
    background-position: center, center;
    background-repeat: no-repeat, no-repeat;
  }

  /* Light theme: the desktop bloom + the baked DARK mobile bloom photo have no
   * light counterpart, so drop them - the light success reads clean on the page
   * (the deepened heading + copy carry the celebration). */
  :root[data-ows-theme='light'] & {
    background-image: none;
  }
`;

export const TopBar = styled.div`
  position: relative;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 32px;

  ${CFG_MOBILE} {
    display: none;
  }
`;

export const Glass = styled.div`
  position: relative;
  z-index: 10;
  display: flex;
  flex: 1;
  overflow: hidden;
  margin: 0 32px 32px;
  border-radius: 20px;
  /* Borderless + transparent for BOTH variants (Figma) - the left reads as the page
     background, the right is its own #100f13 panel. */
  border: none;
  background: transparent;

  ${CFG_MOBILE} {
    /* No card on mobile - full-bleed copy over the bloom. */
    margin: 0;
    flex-direction: column;
    border: none;
    border-radius: 0;
    background: transparent;
  }
`;

/* --------------------------------- left copy -------------------------------- */

export const CopyColumn = styled.div`
  display: flex;
  width: 46%;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 48px;

  ${CFG_MOBILE} {
    width: 100%;
    flex: 1;
    padding: 0 24px;
  }
`;

export const Heading = styled(Text)`
  text-align: center;
  font-size: 48px;
  font-weight: 900;
  /* Gradient text clips to the glyph box, so keep enough leading for descenders (the "g"). */
  line-height: 1.25;
  background-image: ${FINISH_GRADIENTS.successHeading};
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;

  ${CFG_MOBILE} {
    font-size: 38px;
  }
`;

/* "Please verify your email…" - near-white bold per the Figma (matches the
   returning LaunchCopy); muted on mobile (no mobile design supplied). Spacing comes
   from the EmailColumn gap. */
export const Verify = styled(Text)`
  text-align: center;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: 0.04px;
  color: ${CFG.base50};

  ${CFG_MOBILE} {
    font-size: 15px;
    color: ${FINISH.successCopyMuted};
  }
`;

export const PlaneWrap = styled.div`
  width: 320px;
  max-width: 100%;

  ${CFG_MOBILE} {
    width: 280px;
  }
`;

export const PlaneImg = styled.img`
  width: 100%;
  transform: rotate(-36.49deg) scaleY(0.96) skewX(17.02deg);
`;

export const CloseHint = styled(Text)`
  width: 292px;
  max-width: 100%;
  text-align: center;
  font-size: 16px;
  font-weight: 300;
  line-height: 1.1;
  color: ${FINISH.successCloseHint};

  ${CFG_MOBILE} {
    /* Replaced by the "Open Email App" CTA on mobile. */
    display: none;
  }
`;

/* ----------------------------- mobile CTA ----------------------------- */

/* "Open Email App" - mobile only; a white pill pinned below the copy. */
export const OpenMailButton = styled.a`
  display: none;

  ${CFG_MOBILE} {
    position: relative;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 48px;
    margin: 8px 24px calc(24px + env(safe-area-inset-bottom));
    border-radius: 9999px;
    background: ${FINISH.successButtonBg};
    text-decoration: none;
  }
`;

export const OpenMailLabel = styled(Text)`
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  color: ${FINISH.successButtonText};
`;

/* ----------------------- returning-user variant (studio) -------------------- */

/* Body copy for a user who already owns a casino - "personalize … in Owner
   Studio" (replaces the email/verify block). Near-white bold on desktop per the
   Figma (NOT brand-orange); muted on mobile (mirrors the email variant's Verify
   line, no mobile design supplied). Spacing comes from the ReturningColumn gap. */
export const LaunchCopy = styled(Text)`
  width: 100%;
  text-align: center;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: 0.04px;
  color: ${CFG.base50};

  ${CFG_MOBILE} {
    width: 255px;
    font-size: 15px;
    color: ${FINISH.successCopyMuted};
  }
`;

/* "Continue to Owner Studio" - desktop: an inline brand pill in the copy column.
   Vertical spacing is owned by the ReturningColumn gap (no own margin). */
export const StudioButton = styled.button`
  display: flex;
  height: 40px;
  width: 230px;
  max-width: 100%;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 9999px;
  background: ${CFG.brand};

  /* Cursor-following hover glow + lit hairline ring (shared with the CTA Button). */
  ${cardHover}

  ${CFG_MOBILE} {
    display: none;
  }
`;

/* Same CTA, mobile: a brand pill pinned at the bottom (sibling of the card, like
   the email variant's "Open Email App"). */
export const StudioButtonBottom = styled.button`
  display: none;

  ${CFG_MOBILE} {
    position: relative;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 48px;
    margin: 8px 10px calc(24px + env(safe-area-inset-bottom));
    border: none;
    border-radius: 9999px;
    background: ${CFG.brand};
    cursor: pointer;

    /* Cursor-following hover glow + lit hairline ring (shared with the CTA Button). */
    ${cardHover}
  }
`;

export const StudioButtonLabel = styled(Text)`
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: ${FINISH.successButtonText};

  ${CFG_MOBILE} {
    font-weight: 700;
  }
`;

/* Returning-owner copy stack - one centered column with the Figma's uniform 48px
   rhythm (heading → body → CTA → divider → support), constrained to the design's
   426px so the heading wraps to two lines like the frame. Mobile keeps a tighter
   gap and full width. */
export const ReturningColumn = styled.div`
  display: flex;
  width: 426px;
  max-width: 100%;
  flex-direction: column;
  align-items: center;
  gap: 48px;

  ${CFG_MOBILE} {
    width: 100%;
    gap: 24px;
  }
`;

/* Hairline above the support line (both variants) - 360px, faint, per Figma.
   Desktop only; mobile shows the bottom CTA instead (no support line). */
export const SupportDivider = styled.div`
  height: 1px;
  width: 360px;
  max-width: 100%;
  background: linear-gradient(to right, transparent, ${CFG_CHROME.dividerVia}, transparent);

  ${CFG_MOBILE} {
    display: none;
  }
`;

/* "Need help? / support@…" - at the bottom of the copy column (both variants; the
   header keeps only the logo + account icon). Stacked two-line and centered per the
   Figma. Desktop only. */
export const SupportBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;

  ${CFG_MOBILE} {
    display: none;
  }
`;

export const SupportNeedHelp = styled(Text)`
  font-size: 14px;
  font-weight: 400;
  line-height: 1.15;
  color: ${CFG.base300};
`;

export const SupportBlockEmail = styled(Text)`
  font-size: 14px;
  font-weight: 500;
  line-height: 1.15;
  color: ${CFG.brandText};
  opacity: 0.72;
`;

/* While the success screen is mounted (both variants), pull the wizard's global
   account button (UserMenu - fixed top-right) to the viewport edge: its default 422px
   anchor leaves room for the docked sidebar, which this full-bleed screen has not got,
   so the Figma sits it in the corner. Scoped via a CSS var the UserMenu reads;
   unmounts with this screen, so the other wizard steps keep their 422px. */
export const UserMenuEdgeAnchor = createGlobalStyle`
  :root {
    --cfg-usermenu-right: 32px;
  }
`;

/* ----------------------- first-casino variant (verify email) ---------------- */

/* Email-variant copy stack - same centered 426px column + 48px rhythm as the
   returning variant, different content (email chip → verify → plane → divider →
   close + support). Per the Figma. */
export const EmailColumn = styled.div`
  display: flex;
  width: 426px;
  max-width: 100%;
  flex-direction: column;
  align-items: center;
  gap: 48px;

  ${CFG_MOBILE} {
    width: 100%;
    gap: 24px;
  }
`;

/* Heading + email chip sit tight (16px gap) as one unit at the top. */
export const HeadGroup = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

/* Pill showing the address we mailed to - a dark chip with an envelope glyph. */
export const EmailChip = styled.div`
  display: flex;
  height: 40px;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 24px;
  background: ${CFG.base100};
  color: ${CFG.base50};
`;

export const ChipEmail = styled(Text)`
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  white-space: nowrap;
  color: ${CFG.base50};
`;

/* Close hint + support sit tight (24px gap) as one unit at the bottom. Desktop only -
   its children are hidden on mobile (which shows the bottom CTA instead), so the
   wrapper is too, or it would leave a phantom gap slot in the mobile flex column. */
export const CloseSupportGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;

  ${CFG_MOBILE} {
    display: none;
  }
`;

/* ------------------------------- right preview ------------------------------ */

/* Returning-variant preview-pane layout (Figma): the caption sits CAPTION_TOP from
   the top and CAPTION_RIGHT from the right edge, then a CAPTION_GAP gap, then the
   laptop fills down to a PREVIEW_INSET bottom inset, pinned PREVIEW_INSET from the
   left. Its width derives from the laptop aspect ratio, so it bleeds off the right
   (clipped by the pane overflow). */
export const PREVIEW_INSET = 48;
/* Max laptop height (px). Below it the height-fill wins (normal laptop screens are
   unchanged); at/above it the cap holds so the laptop+phone group stops ballooning on
   tall/wide monitors and centres instead. ~600 keeps the laptop:phone proportion close
   to the Finish-step device group (phone fixed at 230px). */
export const LAPTOP_MAX_H = 600;
const CAPTION_TOP = 64;
const CAPTION_RIGHT = 32;
const CAPTION_HEIGHT = 24; // caption line-box (set on PreviewCaption below)
const CAPTION_GAP = 64;
export const LAPTOP_TOP = CAPTION_TOP + CAPTION_HEIGHT + CAPTION_GAP; // 152

export const PreviewColumn = styled.div`
  position: relative;
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 20px;
  background: ${FINISH.successPaneBg};
  padding: 0 40px;

  ${CFG_MOBILE} {
    /* No device preview on mobile. */
    display: none;
  }
`;

/* Both variants: right-anchored per the Figma + a fixed line-box so the 64px
   caption→laptop gap is exact. */
export const PreviewCaption = styled(Text)`
  position: absolute;
  top: ${CAPTION_TOP}px;
  right: ${CAPTION_RIGHT}px;
  font-size: 16px;
  font-weight: 300;
  line-height: ${CAPTION_HEIGHT}px;
  color: ${FINISH.successCloseHint};
`;

/* The laptop+phone GROUP: a box the size of the laptop — width from the finish-frame
   aspect ratio, height filling the pane below the caption down to the bottom inset but
   CAPPED at LAPTOP_MAX_H. VERTICALLY centred via top/bottom + margin-block:auto (the
   extra space when capped is split evenly), so on a tall monitor the capped group sits
   mid-pane. HORIZONTALLY the `$left` (computed in the TSX) is `max(PREVIEW_INSET, centred)`:
   the group is centred while it fits, but once it is wider than the pane it pins to the
   left inset and bleeds off the RIGHT (clipped by the pane overflow) — so the phone at its
   lower-left ALWAYS stays in frame and only the laptop gets cropped, never the phone. The
   phone lives INSIDE this box (see PhoneOnStage) so the two always move together. Box ratio
   from LAPTOP_FRAME_FINISH so the frame + box never desync. Figma. */
export const DeviceStage = styled.div<{ $left: number }>`
  position: absolute;
  top: ${LAPTOP_TOP}px;
  bottom: ${PREVIEW_INSET}px;
  left: ${({ $left }) => $left}px;
  margin-block: auto;
  height: min(calc(100% - ${LAPTOP_TOP + PREVIEW_INSET}px), ${LAPTOP_MAX_H}px);
  width: auto;
  aspect-ratio: ${LAPTOP_FRAME_FINISH.aspectRatio};
`;

/* The phone, positioned relative to the laptop GROUP (not the pane) so it stays stuck to
   the laptop's lower-left corner wherever the group is centred: 24px in from the laptop's
   left edge, and 24px BELOW its bottom edge (the old pane offsets resolved to laptop-left
   +24 / laptop-bottom −24). Fixed width; the child provides the height via its aspect. */
export const PhoneOnStage = styled.div`
  position: absolute;
  left: 24px;
  bottom: -24px;
  z-index: 10;
  width: 230px;
`;

/** Empty laptop-shaped anchor (fills DeviceStage) the preview host floats the warm
 *  desktop iframe over — so the success screen reuses the build flow's iframes. */
export const MacFill = styled.div`
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

/** Empty phone-shaped anchor (fills the PhoneOnStage wrapper) for the warm mobile iframe. */
export const PhoneFill = styled.div`
  width: 100%;
  aspect-ratio: 792 / 1619;
  pointer-events: none;
`;

/** Sized box (same footprint as {@link PhoneFill}) that carries the phone aspect ratio
 *  for the STATIC screenshot variant: PhoneOnStage sets only the width, so the static
 *  device — whose root is 100%×100% — needs this to derive its height (a bare root in
 *  an auto-height parent would collapse). */
export const StaticPhoneFill = styled.div`
  width: 100%;
  aspect-ratio: 792 / 1619;
`;
